function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import Log from '@deephaven/log';
import { assertNotNull } from '@deephaven/utils';
import { useEffect, useMemo, useState } from 'react';
import { useObjectFetch } from "./useObjectFetch.js";
import useDeferredApi from "./useDeferredApi.js";
import useApi from "./useApi.js";
var log = Log.module('useWidget');

/**
 * Types of widgets that can be fetched with this hook.
 */

/**
 * Wrapper object for a widget and error status. Both widget and error will be `null` if it is still loading.
 */

/**
 * Retrieve a widget for the given variable descriptor. Note that if the widget is successfully fetched, ownership of the widget is passed to the consumer and will need to close the object as well.
 * @param descriptor Descriptor or URI to get the widget for. Should be stable to avoid infinite re-fetching.
 * @returns A WidgetWrapper object that contains the widget and JS API, or an error status if there was an issue fetching the widget. Will contain nulls if still loading.
 */
export function useWidget(descriptor) {
  var _useState = useState(() => ({
      widget: null,
      error: null
    })),
    _useState2 = _slicedToArray(_useState, 2),
    wrapper = _useState2[0],
    setWrapper = _useState2[1];
  var objectFetch = useObjectFetch(descriptor);
  var _useDeferredApi = useDeferredApi(descriptor),
    _useDeferredApi2 = _slicedToArray(_useDeferredApi, 2),
    descriptorApi = _useDeferredApi2[0],
    descriptorApiError = _useDeferredApi2[1];
  var defaultApi = useApi();
  var api = descriptorApi !== null && descriptorApi !== void 0 ? descriptorApi : defaultApi;
  useEffect(function loadWidget() {
    log.debug('loadWidget', descriptor);
    var status = objectFetch.status;
    if (status === 'error') {
      // We can't fetch if there's an error getting the fetcher, just return an error
      setWrapper({
        widget: null,
        error: objectFetch.error
      });
      return;
    }
    if (status === 'loading') {
      // Still loading
      setWrapper({
        widget: null,
        error: null
      });
      return;
    }
    var fetch = objectFetch.fetch;
    // We should be able to load the widget. Load it asynchronously, and set the widget when it's done.
    // If we get cancelled before the fetch is done, we should close the widget and its exported objects.
    // If not though, the consumer of the widget is expected to take ownership and close the widget appropriately.
    var isCancelled = false;
    function loadWidgetInternal() {
      return _loadWidgetInternal.apply(this, arguments);
    }
    function _loadWidgetInternal() {
      _loadWidgetInternal = _asyncToGenerator(function* () {
        try {
          assertNotNull(fetch);
          var newWidget = yield fetch();
          if (isCancelled) {
            log.debug2('loadWidgetInternal cancelled', descriptor, newWidget);
            newWidget.close();
            if ('exportedObjects' in newWidget) {
              newWidget.exportedObjects.forEach(exportedObject => {
                exportedObject.close();
              });
            }
            return;
          }
          log.debug('loadWidgetInternal done', descriptor, newWidget);
          setWrapper({
            widget: newWidget,
            error: null
          });
        } catch (e) {
          if (isCancelled) {
            return;
          }
          // We shouldn't log an error from the hook. The onus is on the consumer to log the error from the hook if they want to. However, we can log a debug message for troubleshooting.
          log.debug('loadWidgetInternal error', descriptor, e);
          setWrapper({
            widget: null,
            error: e !== null && e !== void 0 ? e : new Error('Null error')
          });
        }
      });
      return _loadWidgetInternal.apply(this, arguments);
    }
    loadWidgetInternal();
    return () => {
      isCancelled = true;
    };
  }, [descriptor, objectFetch]);
  var wrapperWithApi = useMemo(() => {
    var _wrapper$error;
    return _objectSpread(_objectSpread({}, wrapper), {}, {
      error: (_wrapper$error = wrapper.error) !== null && _wrapper$error !== void 0 ? _wrapper$error : descriptorApiError,
      api: wrapper.widget != null ? api : null
    });
  }, [wrapper, api, descriptorApiError]);
  return wrapperWithApi;
}
export default useWidget;
//# sourceMappingURL=useWidget.js.map